export const DEFAULT_CURRENCY_SYMBOL = '$';
export const DEFAULT_CURRENCY_LOCALE = 'es-CL';

export type CurrencyFormat = {
  /** Symbol to prefix amounts with, e.g. `$`, `L`, `S/`. */
  symbol: string;
  /** BCP 47 tag that decides grouping/decimal separators:
   * `es-CL` → `29.226`, `es-HN` → `29,226`. */
  locale: string;
};

/** Reads how to render money for a subsidiary. Both values are resolved by the
 * backend from its `currency` (symbol) and `country` (number format) — see
 * `suppliers/currency.py` — so the mapping lives in one place: the quotation
 * image the client receives is rendered server-side and needs the same symbol.
 *
 * The fallback is what every environment without those fields renders today
 * (`$`/`es-CL`), so this is safe to ship ahead of the backend. */
export const getCurrencyFormat = (
  symbol?: string | null,
  locale?: string | null
): CurrencyFormat => ({
  symbol: symbol?.trim() || DEFAULT_CURRENCY_SYMBOL,
  locale: locale?.trim() || DEFAULT_CURRENCY_LOCALE,
});

/** Grouping and decimal separators for a locale:
 * `es-CL` → `{ group: '.', decimal: ',' }`, `es-HN` → `{ group: ',', decimal: '.' }`. */
export const getNumberSeparators = (
  locale: string
): { group: string; decimal: string } => {
  const parts = new Intl.NumberFormat(locale).formatToParts(1234.5);
  return {
    group: parts.find((part) => part.type === 'group')?.value ?? '.',
    decimal: parts.find((part) => part.type === 'decimal')?.value ?? ',',
  };
};
